/**
 * D1-backed entity hook (replaces useLocalStorage for business data).
 */
import { useQuery, useQueryClient } from 'react-query';
import {
  fetchAllEntities,
  fetchEntityById,
  createEntity,
  updateEntity,
  deleteEntity,
} from '../services/entityStore';
import { queryKeyForEntity } from '../utils/queryKeys';
import { invalidateFormationGraph } from '../utils/invalidateFormationGraph';

const SPA_ENTITIES_WITH_GRAPH = new Set([
  'courses',
  'trainers',
  'classrooms',
  'training-sessions',
  'sessions',
  'certifications',
]);

export function useEntityStore(entityName) {
  const queryClient = useQueryClient();
  const queryKey = queryKeyForEntity(entityName);

  const { data: items = [], isLoading, error, refetch } = useQuery(
    queryKey,
    () => fetchAllEntities(entityName),
    { staleTime: 15_000, refetchOnWindowFocus: true }
  );

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey });
    if (SPA_ENTITIES_WITH_GRAPH.has(entityName)) {
      await invalidateFormationGraph(queryClient);
    }
  };

  const getById = (id) => items.find((i) => i.id === id) || null;

  const addItem = async (item) => {
    const created = await createEntity(entityName, item);
    await invalidate();
    return created;
  };

  const updateItem = async (item) => {
    const updated = await updateEntity(entityName, item);
    await invalidate();
    return updated;
  };

  const deleteItem = async (id) => {
    await deleteEntity(entityName, id);
    await invalidate();
    return true;
  };

  const refreshData = () => refetch();

  return {
    items,
    isLoading,
    error: error?.message || null,
    getById,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    fetchById: (id) => fetchEntityById(entityName, id),
  };
}

export default useEntityStore;
